import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { HomeHeaderComponent } from '../../components/shared/home-header/home-header.component';
import { HomeSidebarComponent } from '../../components/shared/home-sidebar/home-sidebar.component';
import { OnboardingSlidesComponent } from './onboarding-slides/onboarding-slides.component';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { StaffService } from './onboarding-slides/services/staff.service';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { AppRoutes } from '../../utils/enums/routes.enum';
import { StatusModalComponent } from '../../components/shared/status-modal/status-modal.component';
import { UserStateService } from '@app/services/user-state/user-state.service';
import { NAV_LINKS_HOME, NAV_LINKS_HOME_SADMIN } from '@app/utils/constants/nav-links.constants';

@Component({
  selector: 'app-home-page',
  standalone: true,
  imports: [HomeHeaderComponent, HomeSidebarComponent, RouterModule],
  templateUrl: './home-page.component.html',
  styleUrl: './home-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomePageComponent implements OnInit {
  private readonly staffService = inject(StaffService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  protected readonly bodyContainer = viewChild<ElementRef<HTMLElement>>('bodyContainer');

  public isEditInventoryPage = signal(false);
  
  private userState = inject(UserStateService);
  public links = computed(() => {
      if(this.userState.userData()?.data.roleId === 99) {
        return NAV_LINKS_HOME_SADMIN;
      } else {
        return NAV_LINKS_HOME;
      }
    });
  

  private readonly navigationEndSignal = toSignal(
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
    { initialValue: null },
  );

  constructor() {
    effect(
      () => {
        const event = this.navigationEndSignal();
        this.updateEditPageState(event);
        this.resetScroll();
      },
      { allowSignalWrites: true },
    );
  }

  public ngOnInit(): void {
    this.checkOnboardingStatus();
  }

  private resetScroll(): void {
    const element = this.bodyContainer()?.nativeElement;
    if (element) {
      element.scrollTop = 0;
    }
  }

  private updateEditPageState(event: NavigationEnd | null): void {
    if (!event) return;

    this.isEditInventoryPage.set(event.url.includes(AppRoutes.INVENTORY_EDIT));
  }

  private checkOnboardingStatus(): void {
    this.staffService
      .getPersonalInformation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (!res.data.isOnboardingCompleted) {
            this.dialog.open(OnboardingSlidesComponent, { disableClose: true });
          }
        },
        error: () => {
          this.openErrorModal();
        },
      });
  }

  private openErrorModal(): void {
    this.dialog.open(StatusModalComponent, {
      data: {
        isSuccess: false,
        title: 'onboardingSlides.apiErrors.title',
        description: 'onboardingSlides.apiErrors.fetchPersonalInfo',
      },
    });
  }
}
